import { useEffect, useRef } from 'react'

/** Longest delay `setTimeout` honors; a larger one fires immediately. */
const MAX_TIMER_MS = 2 ** 31 - 1

/**
 * Calls `onPassed` just after `at` passes. A page that judges its printout
 * "not final yet" re-reads the clock here, so a tab left open across an order
 * deadline prints the final copy (PROD-1452).
 *
 * ponytail: an instant more than ~24.8 days out is clamped to the timer cap and
 * not re-armed after it fires, so such a page stays "not final" until its data
 * changes. Re-arm on the cap if far-future ranges ever stay open that long.
 */
export function useOnPassed(at: Date | null, onPassed: () => void): void {
  const callback = useRef(onPassed)
  callback.current = onPassed
  const time = at?.getTime() ?? null
  useEffect(() => {
    if (time === null) {
      return
    }
    const timer = window.setTimeout(
      () => callback.current(),
      Math.min(Math.max(time - Date.now() + 1_000, 0), MAX_TIMER_MS),
    )
    return () => window.clearTimeout(timer)
  }, [time])
}
